import { __assign, __awaiter, __generator } from "tslib";
import { SearchApi } from '../api';
import { DocumentParser, SuggestionsParser } from './parsers';
import { SearchDocumentType, } from './sdk.types';
import { getDemoSearchResponse, getDemoSampleResponse, } from './demoContent';
import { apiErrorParser } from './parsers/apiErrorParser';
var validDocumentTypes = Object.values(SearchDocumentType);
function getSearchApiDocumentType(documentType) {
    if (documentType === SearchDocumentType.All) {
        return;
    }
    return documentType;
}
var getQuery = function (query) { return query || '*'; };
var ClientSearchSDK = /** @class */ (function () {
    function ClientSearchSDK(_a) {
        var httpClient = _a.httpClient, timeout = _a.timeout, siteBaseUrl = _a.siteBaseUrl, language = _a.language, _b = _a.excludeDocumentTypes, excludeDocumentTypes = _b === void 0 ? [] : _b;
        this.language = language;
        this.api = new SearchApi({
            httpClient: httpClient,
            timeout: timeout,
        });
        this.excludeDocumentTypes = excludeDocumentTypes;
        this.documentParser = new DocumentParser(siteBaseUrl);
        this.suggestionsParser = new SuggestionsParser(siteBaseUrl, language);
    }
    ClientSearchSDK.prototype.search = function (searchRequest, requestParams) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var request, data, _b, documents, facets, totalResults, ex_1;
            var _this = this;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 4, , 5]);
                        request = __assign(__assign({}, searchRequest), { documentType: getSearchApiDocumentType(searchRequest.documentType), query: getQuery(searchRequest.query), language: this.language, paging: searchRequest.paging && {
                                skip: (searchRequest.paging.page - 1) * searchRequest.paging.pageSize,
                                limit: searchRequest.paging.pageSize,
                            } });
                        if (!this.demoContentOptions) return [3 /*break*/, 1];
                        _b = getDemoSearchResponse(request, this.demoContentOptions);
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, this.api.search(request, requestParams)];
                    case 2:
                        _b = (_c.sent()).data;
                        _c.label = 3;
                    case 3:
                        data = _b;
                        documents = data.documents.map(function (document) {
                            return _this.documentParser.parse(document);
                        });
                        facets = data.facets;
                        totalResults = data.nextPage ? data.nextPage.total : 0;
                        return [2 /*return*/, {
                                documents: documents,
                                facets: facets,
                                totalResults: totalResults,
                            }];
                    case 4:
                        ex_1 = _c.sent();
                        return [2 /*return*/, {
                                errorDetails: apiErrorParser((_a = ex_1 === null || ex_1 === void 0 ? void 0 : ex_1.response) === null || _a === void 0 ? void 0 : _a.data),
                                isError: true,
                            }];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    ClientSearchSDK.prototype.useDemoContent = function (options) {
        this.demoContentOptions = options;
    };
    ClientSearchSDK.prototype.parseFederatedResults = function (results, titleParser) {
        var _this = this;
        return results
            .filter(function (_a) {
            var documentType = _a.documentType;
            return validDocumentTypes.includes(documentType) &&
                !_this.excludeDocumentTypes.includes(documentType);
        })
            .map(function (sample) { return (__assign(__assign({}, sample), { title: titleParser(sample.documentType), documents: sample.documents.map(function (document) {
                return _this.documentParser.parse(document);
            }) })); });
    };
    ClientSearchSDK.prototype.getFederatedSuggestions = function (suggestionsRequest, requestParams) {
        return __awaiter(this, void 0, void 0, function () {
            var response, results;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.getFederatedSuggestions(__assign(__assign({}, suggestionsRequest), { query: getQuery(suggestionsRequest.query), limit: suggestionsRequest.limit || 10, language: this.language }), requestParams)];
                    case 1:
                        response = _a.sent();
                        results = this.parseFederatedResults(response.data.results, this.suggestionsParser.getDocumentTypeTitle);
                        return [2 /*return*/, {
                                results: results,
                            }];
                }
            });
        });
    };
    ClientSearchSDK.prototype.getTrendingItems = function (trendingRequest, requestParams) {
        return __awaiter(this, void 0, void 0, function () {
            var response, results;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.getTrendingItems(__assign(__assign({}, trendingRequest), { language: this.language }), requestParams)];
                    case 1:
                        response = _a.sent();
                        results = this.parseFederatedResults(response.data.results, this.suggestionsParser.getDocumentTypeTrendingTitle);
                        return [2 /*return*/, { results: results }];
                }
            });
        });
    };
    ClientSearchSDK.prototype.getFederatedAutocomplete = function (autocompleteRequest, requestParams) {
        return __awaiter(this, void 0, void 0, function () {
            var response, results;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.getFederatedAutocomplete(__assign(__assign({}, autocompleteRequest), { query: getQuery(autocompleteRequest.query), limit: autocompleteRequest.limit || 1, language: this.language }), requestParams)];
                    case 1:
                        response = _a.sent();
                        results = response.data.results.filter(function (_a) {
                            var documentType = _a.documentType;
                            return validDocumentTypes.includes(documentType) &&
                                !_this.excludeDocumentTypes.includes(documentType);
                        });
                        return [2 /*return*/, {
                                results: results,
                            }];
                }
            });
        });
    };
    ClientSearchSDK.prototype.getSample = function (sampleRequest, requestParams) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var request, shouldUseEmptyResponse, data, _b, demoDocumentTypes, documentsMapper_1, results, ex_2;
            var _this = this;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 4, , 5]);
                        request = __assign(__assign({}, sampleRequest), { query: sampleRequest.query && getQuery(sampleRequest.query), language: this.language });
                        shouldUseEmptyResponse = this.demoContentOptions &&
                            !this.demoContentOptions.shouldHaveSearchResults;
                        if (!shouldUseEmptyResponse) return [3 /*break*/, 1];
                        _b = { results: [] };
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, this.api.getSample(request, requestParams)];
                    case 2:
                        _b = (_c.sent()).data;
                        _c.label = 3;
                    case 3:
                        data = _b;
                        if (this.demoContentOptions) {
                            demoDocumentTypes = data.results.map(function (_a) {
                                var documentType = _a.documentType;
                                return documentType;
                            });
                            // NOTE: We make a real request in editor demo mode to get available document types
                            // (for tab visibility), but patch actual response documents with demo content.
                            data = getDemoSampleResponse(request, demoDocumentTypes);
                        }
                        documentsMapper_1 = function (document) {
                            return _this.documentParser.parse(document);
                        };
                        results = data.results
                            .filter(function (_a) {
                            var documentType = _a.documentType;
                            return validDocumentTypes.includes(documentType) &&
                                !_this.excludeDocumentTypes.includes(documentType);
                        })
                            .map(function (sample) { return (__assign(__assign({}, sample), { documents: sample.documents.map(documentsMapper_1) })); });
                        return [2 /*return*/, {
                                results: results,
                            }];
                    case 4:
                        ex_2 = _c.sent();
                        return [2 /*return*/, {
                                errorDetails: apiErrorParser((_a = ex_2 === null || ex_2 === void 0 ? void 0 : ex_2.response) === null || _a === void 0 ? void 0 : _a.data),
                                isError: true,
                            }];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    return ClientSearchSDK;
}());
export { ClientSearchSDK };
//# sourceMappingURL=sdk.js.map